import React from 'react';
import { Alert, Card, Chip, Grid, Switch, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService } from '../api';
import { ApiError } from '../api/core/ApiError';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';
import { formatMoney, withBookingFee } from '../common/bookingFee';
import { mb } from '../common/theme/mbTokens';

const EXAMPLE_PRICE = 100;

type BookingFeeCardProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
};

const ExampleTile = ({ label, value }: { label: string; value: string }) => (
  <Grid
    container
    flexDirection="column"
    gap={0.5}
    sx={{ p: 1.5, height: '100%', bgcolor: mb.ground, borderRadius: `${mb.radius.card}px` }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={700}>
      {value}
    </Typography>
  </Grid>
);

export const BookingFeeCard = ({ account, updateLocalAccount }: BookingFeeCardProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const bookingFee = account.booking_fee;
  const isAvailable = !!bookingFee?.available;
  const isEnabled = isAvailable && !!bookingFee?.enabled;
  const ratePercent = bookingFee?.rate_percent ?? 0;
  const examplePriceAccount = { ...account, booking_fee: { ...bookingFee, enabled: true, available: true } };

  const getErrorMessage = (error: ApiError) =>
    typeof error.body?.detail === 'string' ? error.body.detail : t('api.error.default');

  const handleToggle = async (enabled: boolean) => {
    try {
      const updatedAccount = await safeExecute({
        apiCall: () => AccountService.updateBookingFee({ enabled }),
        successMessage: '',
        suppressSuccessMessage: true,
        onErrorMessageGenerator: getErrorMessage,
      });
      await updateLocalAccount(updatedAccount);
    } catch (error) {
      // safeExecute already showed the error; the switch keeps its saved value
    }
  };

  return (
    <Card
      sx={{
        p: mb.layout.cardPadding,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        ...(!isAvailable && { bgcolor: mb.ground, boxShadow: 'none' }),
      }}>
      <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap" gap={1.5}>
        <Typography variant="h6" fontWeight={700} color={isAvailable ? undefined : 'text.secondary'}>
          {t('payments.bookingFee.title')}
        </Typography>
        {isAvailable ? (
          <Chip
            size="small"
            label={isEnabled ? t('payments.bookingFee.on') : t('payments.bookingFee.off')}
            sx={{
              bgcolor: isEnabled ? mb.mint : mb.ground,
              color: isEnabled ? mb.green : mb.muted,
              fontWeight: 500,
              borderRadius: `${mb.radius.chip}px`,
            }}
          />
        ) : (
          <LockOutlined fontSize="small" sx={{ color: mb.muted }} />
        )}
      </Grid>

      {isAvailable ? (
        <Typography>{t('payments.bookingFee.lead', { rate: ratePercent })}</Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('payments.bookingFee.locked')}
        </Typography>
      )}

      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        flexWrap="nowrap"
        gap={2}
        sx={{
          py: 1.25,
          borderTop: `1px solid ${mb.lineSoft}`,
          borderBottom: isAvailable ? `1px solid ${mb.lineSoft}` : 0,
        }}>
        <Grid container flexDirection="column" gap={0.25}>
          <Typography fontWeight={500} color={isAvailable ? undefined : 'text.secondary'}>
            {t('payments.bookingFee.switch')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('payments.bookingFee.switchHint')}
          </Typography>
        </Grid>
        <Switch
          checked={isEnabled}
          disabled={!isAvailable}
          onChange={(event) => {
            void handleToggle(event.target.checked);
          }}
          inputProps={{ 'aria-label': t('payments.bookingFee.switch') }}
        />
      </Grid>

      {isAvailable && (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <ExampleTile label={t('payments.bookingFee.yourPrice')} value={formatMoney(EXAMPLE_PRICE, account)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ExampleTile
                label={t('payments.bookingFee.customersPay')}
                value={formatMoney(withBookingFee(EXAMPLE_PRICE, examplePriceAccount), account)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ExampleTile
                label={t('payments.bookingFee.youReceive')}
                value={t('payments.bookingFee.sameAsToday', { price: formatMoney(EXAMPLE_PRICE, account) })}
              />
            </Grid>
          </Grid>
          <Alert severity="info">{t('payments.bookingFee.notBilled')}</Alert>
        </>
      )}
    </Card>
  );
};
